// src/components/RealEngineHealthWidget.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  Collapse,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  ExpandMore,
  ExpandLess,
  BugReport,
  Thermostat,
  Speed,
} from '@mui/icons-material';

interface DtcCode {
  code: string;
  description: string;
}

interface Issue {
  type: string;
  severity: string;
  message: string;
  value?: number;
  codes?: string[];
  codesWithDescriptions?: DtcCode[];
}

interface VehicleHealth {
  imei: string;
  healthScore: number;
  status: string;
  issues: Issue[];
  device?: {
    plateNumber?: string;
    make?: string;
    model?: string;
  };
}

interface Props {
  data: VehicleHealth[];
}

const issueIcon: Record<string, React.ReactNode> = {
  dtc_codes: <BugReport fontSize="small" />,
  overheating: <Thermostat fontSize="small" />,
  high_temp: <Thermostat fontSize="small" />,
  high_load: <Speed fontSize="small" />,
  battery_critical: <Warning fontSize="small" />,
  battery_low: <Warning fontSize="small" />,
};

const VehicleCard: React.FC<{ vehicle: VehicleHealth }> = ({ vehicle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedIssue, setExpandedIssue] = useState<number | null>(null);

  const borderColor =
    vehicle.status === 'critical'
      ? theme.palette.error.main
      : theme.palette.warning.main;

  const bgColor =
    vehicle.status === 'critical'
      ? alpha(theme.palette.error.main, 0.05)
      : alpha(theme.palette.warning.main, 0.05);

  return (
    <Box
      sx={{
        mb: 1,
        p: isMobile ? 0.75 : 1,
        bgcolor: bgColor,
        borderRadius: 1,
        borderLeft: `4px solid ${borderColor}`,
      }}
    >
      {/* Vehicle header */}
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? 12 : 14 }}>
        {vehicle.device?.plateNumber || vehicle.imei}
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? 10 : 12 }}>
        Score: {vehicle.healthScore} · {vehicle.issues.length} issue(s)
      </Typography>

      {/* Issues list */}
      <Box sx={{ mt: 0.5 }}>
        {vehicle.issues.map((issue, i) => {
          const hasDTC =
            issue.type === 'dtc_codes' &&
            issue.codesWithDescriptions &&
            issue.codesWithDescriptions.length > 0;
          const isExpanded = expandedIssue === i;

          return (
            <Box key={i}>
              {/* Issue row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.25,
                  cursor: hasDTC ? 'pointer' : 'default',
                }}
                onClick={() => hasDTC && setExpandedIssue(isExpanded ? null : i)}
              >
                <Box
                  sx={{
                    color:
                      issue.severity === 'critical'
                        ? theme.palette.error.main
                        : theme.palette.warning.main,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  {issueIcon[issue.type] || <Warning fontSize="small" />}
                </Box>
                <Typography
                  variant="caption"
                  color={issue.severity === 'critical' ? 'error.main' : 'warning.main'}
                  sx={{
                    fontSize: isMobile ? 10 : 11,
                    fontWeight: 500,
                    flex: 1,
                  }}
                >
                  {issue.message}
                </Typography>
                {hasDTC && (
                  <IconButton size="small" sx={{ p: 0, ml: 'auto' }}>
                    {isExpanded ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                )}
              </Box>

              {/* DTC code expansion */}
              {hasDTC && (
                <Collapse in={isExpanded}>
                  <Box
                    sx={{
                      ml: 2.5,
                      mt: 0.5,
                      mb: 0.5,
                      p: 1,
                      bgcolor: alpha(theme.palette.error.main, 0.05),
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
                    }}
                  >
                    {issue.codesWithDescriptions!.map((dtc, di) => (
                      <Box
                        key={di}
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'flex-start',
                          mb: di < issue.codesWithDescriptions!.length - 1 ? 0.75 : 0,
                        }}
                      >
                        <Chip
                          label={dtc.code}
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{
                            fontSize: 10,
                            height: 18,
                            flexShrink: 0,
                            fontFamily: 'monospace',
                            fontWeight: 700,
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? 9 : 10, lineHeight: 1.4 }}
                        >
                          {dtc.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const RealEngineHealthWidget: React.FC<Props> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!data || data.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="textSecondary" variant="body2" textAlign="center">
          No engine health data available
        </Typography>
      </Box>
    );
  }

  const summary = data.reduce(
    (acc, vehicle) => {
      acc[vehicle.status] = (acc[vehicle.status] || 0) + 1;
      acc.totalScore += vehicle.healthScore;
      return acc;
    },
    { healthy: 0, warning: 0, critical: 0, totalScore: 0 } as Record<string, number>
  );

  const avgScore = Math.round(summary.totalScore / data.length);

  const criticalVehicles = data
    .filter((v) => v.status === 'critical' || v.status === 'warning')
    .sort((a, b) => a.healthScore - b.healthScore)
    .slice(0, isMobile ? 2 : 3);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Score */}
      <Box>
        <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ fontWeight: 'bold', mb: 1 }}>
          {avgScore}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Average Health Score
        </Typography>
        <LinearProgress
          variant="determinate"
          value={avgScore}
          sx={{
            height: isMobile ? 6 : 8,
            borderRadius: 4,
            backgroundColor: '#f0f0f0',
            '& .MuiLinearProgress-bar': {
              backgroundColor:
                avgScore >= 80
                  ? theme.palette.success.main
                  : avgScore >= 60
                  ? theme.palette.warning.main
                  : theme.palette.error.main,
              borderRadius: 4,
            },
          }}
        />
      </Box>

      {/* Status chips */}
      <Stack direction={isMobile ? 'column' : 'row'} spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
        <Chip
          icon={<CheckCircle />}
          label={`${summary.healthy || 0} Healthy`}
          color="success"
          size="small"
          sx={{ fontSize: isMobile ? 11 : 13 }}
        />
        <Chip
          icon={<Warning />}
          label={`${summary.warning || 0} Warning`}
          color="warning"
          size="small"
          sx={{ fontSize: isMobile ? 11 : 13 }}
        />
        <Chip
          icon={<Error />}
          label={`${summary.critical || 0} Critical`}
          color="error"
          size="small"
          sx={{ fontSize: isMobile ? 11 : 13 }}
        />
      </Stack>

      {/* Vehicles needing attention */}
      {criticalVehicles.length > 0 && (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontSize: isMobile ? 12 : 14 }}>
            Needs Attention:
          </Typography>
          {criticalVehicles.map((vehicle, idx) => (
            <VehicleCard key={idx} vehicle={vehicle} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RealEngineHealthWidget;